import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaUserAlt,
  FaRegEdit,
  FaTrash,
  FaPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaPercentage,
  FaBirthdayCake,
  FaExclamationTriangle,
  FaInfoCircle,
} from 'react-icons/fa';
import { MdMilitaryTech } from 'react-icons/md';
import { MdElderly } from 'react-icons/md';
import { RiVipCrownFill, RiShieldUserFill } from 'react-icons/ri';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Modal Components
const PolicyModal = ({
  isOpen,
  onClose,
  title,
  isEdit = false,
  formData,
  setFormData,
  onSubmit,
  loading,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsVisible(true);
    } else {
      document.body.style.overflow = 'unset';
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  if (!isVisible && !isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300`}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-900/80 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        />

        {/* Modal panel */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-100 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gray-300 rounded-lg">
                  <MdElderly className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-black">{title}</h3>
                  <p className="text-sm text-gray-950 mt-1">
                    {isEdit
                      ? 'Update policy details below'
                      : 'Configure senior citizen discount policy details'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-950 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <form onSubmit={onSubmit}>
            <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Policy Name */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-900">Policy Name</label>
                  <span className="text-xs text-gray-500">{formData.name.length}/255</span>
                </div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                  placeholder="e.g., Senior Citizen Special Discount"
                  required
                  minLength={3}
                  maxLength={255}
                />
                <div className="flex items-start space-x-2 p-2 bg-gray-50 rounded-lg">
                  <FaInfoCircle className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600">
                    A descriptive name for this discount policy (3-255 characters)
                  </p>
                </div>
              </div>

              {/* Age Requirement */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-900">
                  <div className="flex items-center space-x-2">
                    <FaBirthdayCake className="text-gray-600" />
                    <span>Minimum Age Requirement</span>
                  </div>
                </label>

                {/* Age Display */}
                <div className="flex items-center justify-center space-x-3 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">{formData.age_threshold}</div>
                    <div className="text-sm text-gray-600">years</div>
                  </div>
                  <div className="text-gray-400">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="text-sm text-gray-600 max-w-xs">
                    Guests must be at least{' '}
                    <span className="font-semibold text-gray-900">
                      {formData.age_threshold} years
                    </span>{' '}
                    old to qualify
                  </div>
                </div>

                {/* Age Slider */}
                <div className="space-y-2">
                  <input
                    type="range"
                    name="age_threshold"
                    min="50"
                    max="100"
                    step="1"
                    value={formData.age_threshold}
                    onChange={handleInputChange}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gray-900 [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-lg"
                  />
                  <div className="flex justify-between text-sm text-gray-600 px-1">
                    <span>50</span>
                    <span>60</span>
                    <span>70</span>
                    <span>80</span>
                    <span>90</span>
                    <span>100</span>
                  </div>
                </div>
              </div>

              {/* Discount Percentage */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-900">
                  <div className="flex items-center space-x-2">
                    <FaPercentage className="text-gray-600" />
                    <span>Discount Percentage</span>
                  </div>
                </label>

                {/* Discount Display */}
                <div className="flex items-center justify-center space-x-3 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      {formData.discount_percent}%
                    </div>
                    <div className="text-sm text-gray-600">OFF</div>
                  </div>
                  <div className="text-blue-400">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="text-sm text-gray-600 max-w-xs">
                    Maximum discount allowed is{' '}
                    <span className="font-semibold text-gray-900">50%</span>. This will be applied
                    to the base room rate.
                  </div>
                </div>

                {/* Discount Slider */}
                <div className="space-y-2">
                  <input
                    type="range"
                    name="discount_percent"
                    min="0"
                    max="50"
                    step="0.5"
                    value={formData.discount_percent}
                    onChange={handleInputChange}
                    className="w-full h-3 bg-blue-100 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-lg"
                  />
                  <div className="flex justify-between text-sm text-gray-600 px-1">
                    <span>0%</span>
                    <span>10%</span>
                    <span>20%</span>
                    <span>30%</span>
                    <span>40%</span>
                    <span>50%</span>
                  </div>
                </div>
              </div>

              {/* Policy Status */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Policy Status</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {formData.is_active
                        ? 'This policy will be immediately available for bookings'
                        : 'Policy will be saved but not available for bookings'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span
                      className={`text-sm font-medium ${formData.is_active ? 'text-green-600' : 'text-gray-500'}`}
                    >
                      {formData.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, is_active: !prev.is_active }))
                      }
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                        formData.is_active ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          formData.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isEdit ? 'Saving...' : 'Creating...'}
                  </span>
                ) : (
                  <span className="flex items-center">
                    {isEdit ? (
                      <>
                        <FaRegEdit className="mr-2 h-4 w-4" />
                        Update Policy
                      </>
                    ) : (
                      <>
                        <FaPlus className="mr-2 h-4 w-4" />
                        Create Policy
                      </>
                    )}
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmationModal = ({ isOpen, onClose, policyToDelete, onConfirm, loading }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsVisible(true);
    } else {
      document.body.style.overflow = 'unset';
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isVisible && !isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300`}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-900/80 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          {/* Header */}
          <div className="bg-red-50 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FaExclamationTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Deactivate Policy</h3>
                  <p className="text-sm text-gray-600 mt-1">Confirm policy deactivation</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-red-100 rounded-lg transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="px-6 py-5">
            {policyToDelete && (
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 mb-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-white rounded-lg">
                    <MdElderly className="h-5 w-5 text-gray-900" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">{policyToDelete.name}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-600 mt-1">
                      <span className="flex items-center">
                        <FaBirthdayCake className="mr-1 h-3 w-3" />
                        Age: {policyToDelete.age_threshold}+
                      </span>
                      <span className="flex items-center">
                        <FaPercentage className="mr-1 h-3 w-3" />
                        Discount: {policyToDelete.discount_percent}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-start space-x-2">
                  <FaExclamationTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-red-800 font-medium mb-1">
                      Are you sure you want to deactivate this policy?
                    </p>
                    <p className="text-sm text-red-700">
                      This action will remove the policy from active bookings and guest selections.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <div className="p-1 bg-gray-100 rounded">
                    <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                  </div>
                  <p className="text-sm text-gray-600">
                    This policy will be removed from active bookings
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="p-1 bg-gray-100 rounded">
                    <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Existing reservations will not be affected
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="p-1 bg-gray-100 rounded">
                    <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                  </div>
                  <p className="text-sm text-gray-600">You can reactivate it later if needed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="px-6 py-3 text-sm font-medium text-white bg-red-600 border border-transparent rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Deactivating...
                </span>
              ) : (
                <span className="flex items-center">
                  <FaTimesCircle className="mr-2 h-4 w-4" />
                  Deactivate Policy
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const Policies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('senior');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    age_threshold: 60,
    discount_percent: 10,
    is_active: true,
  });
  const [modalLoading, setModalLoading] = useState(false);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BQ_BASE_URL}/bq/api/senior-discount-policies/`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch policies');

      const result = await response.json();
      if (result.data) {
        setPolicies(result.data);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'senior') {
      fetchPolicies();
    }
  }, [activeTab]);

  const handleCreatePolicy = async (e) => {
    e.preventDefault();
    setModalLoading(true);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/senior-discount-policies/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          age_threshold: parseInt(formData.age_threshold),
          discount_percent: parseFloat(formData.discount_percent),
          is_active: formData.is_active,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create policy');
      }

      toast.success('Policy created successfully!');
      setShowCreateModal(false);
      setFormData({
        name: '',
        age_threshold: 60,
        discount_percent: 10,
        is_active: true,
      });
      fetchPolicies();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdatePolicy = async (e) => {
    e.preventDefault();
    if (!editingPolicy) return;
    setModalLoading(true);

    try {
      const updateData = {};
      if (formData.name !== editingPolicy.name) updateData.name = formData.name.trim();
      if (parseInt(formData.age_threshold) !== editingPolicy.age_threshold)
        updateData.age_threshold = parseInt(formData.age_threshold);
      if (parseFloat(formData.discount_percent) !== editingPolicy.discount_percent)
        updateData.discount_percent = parseFloat(formData.discount_percent);
      if (formData.is_active !== editingPolicy.is_active) updateData.is_active = formData.is_active;

      if (Object.keys(updateData).length === 0) {
        toast.info('No changes detected');
        setShowEditModal(false);
        setModalLoading(false);
        return;
      }

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/senior-discount-policies/${editingPolicy.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update policy');
      }

      toast.success('Policy updated successfully!');
      setShowEditModal(false);
      setEditingPolicy(null);
      fetchPolicies();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeletePolicy = async () => {
    if (!policyToDelete) return;
    setModalLoading(true);

    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/senior-discount-policies/${policyToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to deactivate policy');
      }

      toast.success('Policy deactivated successfully!');
      setShowDeleteModal(false);
      setPolicyToDelete(null);
      fetchPolicies();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteClick = (policy) => {
    setPolicyToDelete(policy);
    setShowDeleteModal(true);
  };

  const handleEditClick = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      name: policy.name,
      age_threshold: policy.age_threshold,
      discount_percent: policy.discount_percent,
      is_active: policy.is_active,
    });
    setShowEditModal(true);
  };

  const togglePolicyStatus = async (policy) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/senior-discount-policies/${policy.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          is_active: !policy.is_active,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update policy status');
      }

      toast.success(`Policy ${!policy.is_active ? 'activated' : 'deactivated'} successfully!`);
      fetchPolicies();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const PolicyCard = ({ policy }) => {
    const isActive = policy.is_active;

    return (
      <div
        className={`bg-white border ${isActive ? 'border-gray-200' : 'border-gray-300'} rounded-xl shadow-sm hover:shadow-md transition-all duration-200`}
      >
        <div className="p-5">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center space-x-3">
              <div className={`p-2.5 ${isActive ? 'bg-gray-100' : 'bg-gray-50'} rounded-lg`}>
                <MdElderly className="h-5 w-5 text-gray-900" />
              </div>
              <div>
                <h6 className="font-semibold text-gray-900">{policy.name}</h6>
                <div className="flex items-center mt-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex space-x-1">
              <button
                onClick={() => handleEditClick(policy)}
                className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Edit policy"
              >
                <FaRegEdit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDeleteClick(policy)}
                className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Deactivate policy"
              >
                <FaTrash className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Policy Details */}
          <div className="space-y-4 pt-4 border-t border-gray-100">
            {/* Age Requirement */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="p-2 bg-white rounded-lg mr-3">
                  <FaBirthdayCake className="h-4 w-4 text-gray-900" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">Age Requirement</div>
                  <div className="text-xs text-gray-500">Minimum age to qualify</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">{policy.age_threshold}</div>
                <div className="text-xs text-gray-500">years</div>
              </div>
            </div>

            {/* Discount Rate */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="p-2 bg-white rounded-lg mr-3">
                  <FaPercentage className="h-4 w-4 text-gray-900" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">Discount Rate</div>
                  <div className="text-xs text-gray-500">Applied to base rate</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">{policy.discount_percent}%</div>
                <div className="text-xs text-gray-500">OFF</div>
              </div>
            </div>
          </div>

          {/* Status Info */}
          <div
            className={`mt-4 p-3 rounded-lg text-sm ${
              isActive
                ? 'bg-gray-50 text-gray-900 border border-gray-200'
                : 'bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            {isActive ? (
              <div className="flex items-center">
                <FaCheckCircle className="mr-2 h-4 w-4 text-green-500" />
                <span>This policy is currently active and available for bookings.</span>
              </div>
            ) : (
              <div className="flex items-center">
                <FaTimesCircle className="mr-2 h-4 w-4 text-gray-500" />
                <span>This policy is inactive and not available for bookings.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const FeatureCard = ({ title, description, icon: Icon }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-gray-300 transition-colors">
      <div className="flex items-start space-x-4">
        <div className="p-3 bg-gray-100 rounded-lg">
          <Icon className="h-6 w-6 text-gray-900" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
          <p className="text-sm text-gray-600 mb-4">{description}</p>
          <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-900 border border-gray-300">
            Coming Soon
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Discount Policies</h2>
            <p className="text-gray-600 mt-1">
              Manage special discounts and loyalty programs for your guests
            </p>
          </div>
          {activeTab === 'senior' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2.5 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
            >
              <FaPlus className="mr-2 h-4 w-4" />
              New Policy
            </button>
          )}
        </div>

        {/* Policy Type Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('senior')}
              className={`${
                activeTab === 'senior'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors`}
            >
              <FaUserAlt className="h-4 w-4" />
              <span>Senior Citizen</span>
            </button>
            <button
              onClick={() => setActiveTab('military')}
              className={`${
                activeTab === 'military'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors`}
            >
              <MdMilitaryTech className="h-4 w-4" />
              <span>Military & Veterans</span>
            </button>
            <button
              onClick={() => setActiveTab('membership')}
              className={`${
                activeTab === 'membership'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors`}
            >
              <RiVipCrownFill className="h-4 w-4" />
              <span>Membership Programs</span>
            </button>
          </nav>
        </div>

        {/* Senior Citizen Discount Content */}
        {activeTab === 'senior' && (
          <div className="space-y-8">
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Active Policies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Policies currently available for guest bookings. Only one policy can be active
                    at a time.
                  </p>
                </div>
                <span className="text-sm text-gray-700 bg-gray-100 px-3 py-1.5 rounded-full font-medium">
                  {policies.filter((p) => p.is_active).length} active
                </span>
              </div>

              {policies.filter((p) => p.is_active).length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-10 text-center">
                  <div className="max-w-md mx-auto">
                    <div className="p-4 bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-5">
                      <FaUserAlt className="h-8 w-8 text-gray-900" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      No Active Senior Citizen Policies
                    </h4>
                    <p className="text-gray-600 mb-6">
                      Create a senior citizen discount policy to offer special rates for guests aged
                      50 years and above.
                    </p>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="inline-flex items-center px-5 py-2.5 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
                    >
                      <FaPlus className="mr-2 h-4 w-4" />
                      Create First Policy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {policies
                    .filter((p) => p.is_active)
                    .map((policy) => (
                      <PolicyCard key={policy.id} policy={policy} />
                    ))}
                </div>
              )}
            </div>

            {policies.filter((p) => !p.is_active).length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Inactive Policies</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Archived policies no longer available for new bookings
                    </p>
                  </div>
                  <span className="text-sm text-gray-700 bg-gray-100 px-3 py-1.5 rounded-full font-medium">
                    {policies.filter((p) => !p.is_active).length} archived
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {policies
                    .filter((p) => !p.is_active)
                    .map((policy) => (
                      <PolicyCard key={policy.id} policy={policy} />
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Military & Veterans Tab */}
        {activeTab === 'military' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-10">
              <div className="max-w-2xl mx-auto text-center">
                <div className="p-5 bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-gray-200">
                  <MdMilitaryTech className="h-10 w-10 text-gray-900" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Military & Veteran Discounts
                </h3>
                <p className="text-gray-600 mb-8 text-lg">
                  Honor those who serve with special rates for military personnel and veterans.
                  Configure verification methods, discount tiers, and eligibility criteria.
                </p>
                <div className="inline-flex items-center px-5 py-2.5 rounded-xl bg-gray-100 text-gray-900 font-medium border border-gray-300">
                  <span className="animate-pulse mr-2">●</span>
                  Feature Launching Q2 2028
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FeatureCard
                title="Active Military Discount"
                description="Special rates for currently serving military personnel with ID verification. Support for all branches."
                icon={MdMilitaryTech}
              />
              <FeatureCard
                title="Veteran & Retiree Programs"
                description="Lifetime discounts for retired military veterans. Flexible verification options available."
                icon={RiShieldUserFill}
              />
            </div>
          </div>
        )}

        {/* Membership Programs Tab */}
        {activeTab === 'membership' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-10">
              <div className="max-w-2xl mx-auto text-center">
                <div className="p-5 bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-gray-200">
                  <RiVipCrownFill className="h-10 w-10 text-gray-900" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Membership & Loyalty Programs
                </h3>
                <p className="text-gray-600 mb-8 text-lg">
                  Reward your loyal guests with tiered membership programs. Set up points systems,
                  member-only benefits, and automated discounts based on stay history and frequency.
                </p>
                <div className="inline-flex items-center px-5 py-2.5 rounded-xl bg-gray-100 text-gray-900 font-medium border border-gray-300">
                  <span className="animate-pulse mr-2">●</span>
                  Feature Launching Q3 2028
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FeatureCard
                title="Bronze Tier"
                description="Entry-level membership with occasional discounts, early check-in options, and welcome amenities."
                icon={RiVipCrownFill}
              />
              <FeatureCard
                title="Silver Tier"
                description="Regular discounts, priority booking, complimentary breakfast, and room upgrades when available."
                icon={RiVipCrownFill}
              />
              <FeatureCard
                title="Gold Tier"
                description="Premium benefits including suite upgrades, spa credits, late checkout, and exclusive event access."
                icon={RiVipCrownFill}
              />
            </div>
          </div>
        )}
      </div>

      {/* Modals - rendered at root level */}
      <PolicyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Senior Citizen Discount Policy"
        isEdit={false}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleCreatePolicy}
        loading={modalLoading}
      />

      <PolicyModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPolicy(null);
        }}
        title="Edit Senior Citizen Policy"
        isEdit={true}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleUpdatePolicy}
        loading={modalLoading}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setPolicyToDelete(null);
        }}
        policyToDelete={policyToDelete}
        onConfirm={handleDeletePolicy}
        loading={modalLoading}
      />
    </>
  );
};

export default Policies;
